import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaAward, FaGlobe, FaCode, FaRocket, FaArrowRight, FaMicrosoft, FaCloud, FaCheckCircle } from 'react-icons/fa';
import './AboutPage.css';

const AboutPage: React.FC = () => {
    const navigate = useNavigate();
    const [scrollY, setScrollY] = useState(0);
    useEffect(() => {
        const fn = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', fn, { passive: true });
        return () => window.removeEventListener('scroll', fn);
    }, []);

    const values = [
        { icon: <FaRocket />, title: 'Innovation First', desc: 'We continuously embrace emerging technologies to keep your business ahead.' },
        { icon: <FaUsers />, title: 'Client-Centric', desc: 'Your goals drive every decision. We succeed when you succeed.' },
        { icon: <FaAward />, title: 'Quality-Driven', desc: 'Uncompromising standards in every line of code and every deployment.' },
        { icon: <FaGlobe />, title: 'Global Mindset', desc: 'Dutch headquarters, European expertise, globally scalable solutions.' },
    ];

    const expertise = [
        { icon: <FaMicrosoft />, cat: 'Microsoft', items: ['SharePoint', 'Power Apps', 'Power Automate', 'Power BI', 'Microsoft Copilot', 'Office 365'] },
        { icon: <FaCode />, cat: 'Development', items: ['React.js', '.NET / C#', 'Node.js', 'Flutter', 'REST APIs', 'TypeScript'] },
        { icon: <FaCloud />, cat: 'Cloud & Data', items: ['Microsoft Azure', 'SQL Server', 'Dataverse', 'MySQL', 'Docker', 'DevOps'] },
    ];

    const timeline = [
        { year: '2025', title: 'Founded in the Netherlands', desc: 'CoreNuVate IT was officially established in Hoek van Holland with a mission to deliver enterprise-grade digital transformation.' },
        { year: '2025', title: 'Core Team Assembled', desc: 'Brought together a team of Microsoft-certified engineers, cloud architects, and full-stack developers ready to serve enterprise clients.' },
        { year: '2025', title: 'Service Portfolio Launched', desc: 'Rolled out our full service offering — Microsoft 365, Power Platform, Azure cloud, web & mobile development, and AI automation.' },
        { year: '2025', title: 'First Client Engagements', desc: 'Secured our first enterprise partnerships, delivering SharePoint modernisation and Power Platform solutions.' },
        { year: '2026+', title: 'Scaling for Growth', desc: 'Actively expanding our client base, building strategic partnerships, and investing in AI-powered delivery capabilities.' },
    ];

    const stats = [
        { num: '15+', label: 'Enterprise Technologies' },
        { num: '10', label: 'Active Projects' },
        { num: '24/7', label: 'Support Available' },
    ];

    return (
        <main className="about-page">
            {/* Page Banner with parallax team image */}
            <section className="page-banner about-banner">
                <div className="about-banner-img-wrap">
                    <img
                        src="/images/about_team.png"
                        alt="CoreNuVate team"
                        className="about-banner-img"
                        style={{ transform: `scale(1.08) translateY(${scrollY * 0.15}px)` }}
                    />
                    <div className="about-banner-overlay" />
                </div>
                <div className="container">
                    <span className="page-banner-tag">About CoreNuVate</span>
                    <h1 className="page-banner-title fade-in-up">
                        Building the Future<br /><span className="gradient-text">of Enterprise IT</span>
                    </h1>
                    <p className="page-banner-subtitle fade-in-up delay-1">
                        A premier Netherlands-based technology firm specialising in digital modernisation,
                        cloud migration, and intelligent software solutions.
                    </p>
                </div>
            </section>

            {/* ── Mission & Story ── */}
            <section className="section">
                <div className="container">
                    <div className="mission-layout">
                        <div className="mission-left">
                            <span className="section-label">Our Story</span>
                            <h2 className="section-title">Born to Innovate.<br /><span>Built for Enterprise.</span></h2>
                            <p style={{ color: '#94A3B8', lineHeight: 1.8, marginBottom: 16 }}>
                                Founded by Catherine Dullaart in 2025, CoreNuVate IT was created with a clear mission:
                                to give businesses a technology partner that truly understands their challenges and delivers measurable results.
                            </p>
                            <p style={{ color: '#94A3B8', lineHeight: 1.8, marginBottom: 16 }}>
                                Based in Hoek van Holland, we bring together a handpicked team of Microsoft-certified consultants,
                                cloud architects, and full-stack developers — ready to help enterprises modernise legacy systems,
                                migrate to the cloud, and unlock the power of AI-driven automation.
                            </p>
                            <p style={{ color: '#94A3B8', lineHeight: 1.8 }}>
                                With deep expertise in Microsoft technologies, modern web frameworks, and integration platforms,
                                we've built a reputation for quality, agility, and long-term partnership.
                            </p>
                        </div>
                        <div className="mission-right">
                            <div className="metrics-strip">
                                {stats.map(s => (
                                    <div key={s.label} className="metric-item">
                                        <div className="metric-number">{s.num}</div>
                                        <div className="metric-label">{s.label}</div>
                                    </div>
                                ))}
                            </div>
                            {/* Team Collaboration Image */}
                            <div className="mission-img-block">
                                <img src="/images/about_mission.png" alt="Team working" className="mission-img" />
                                <div className="mission-img-badge">
                                    <FaCheckCircle className="mission-badge-icon" />
                                    <div>
                                        <div className="float-label">Microsoft Partner</div>
                                        <div className="float-value">Certified Experts</div>
                                    </div>
                                </div>
                            </div>
                            {/* Founder Card */}
                            <div className="founder-card glass-card">
                                <img src="/Catherine.jpg" alt="Catherine Dullaart" className="founder-img" />
                                <div>
                                    <h4>Catherine Dullaart</h4>
                                    <p className="founder-title">Founder & CEO</p>
                                    <p className="founder-bio">
                                        With deep expertise in enterprise technology and digital transformation, Catherine leads
                                        CoreNuVate with a vision to empower businesses with future-ready solutions.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Core Values ── */}
            <section className="section about-values-section">
                <div className="container">
                    <div className="text-center mb-64">
                        <span className="section-label">What Drives Us</span>
                        <h2 className="section-title">Our Core <span>Values</span></h2>
                    </div>
                    <div className="values-grid">
                        {values.map((v, i) => (
                            <div key={i} className="glass-card value-card">
                                <div className="icon-box">{v.icon}</div>
                                <h3>{v.title}</h3>
                                <p>{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Expertise ── */}
            <section className="section">
                <div className="container">
                    <div className="text-center mb-64">
                        <span className="section-label">Technical Excellence</span>
                        <h2 className="section-title">Our Core <span>Expertise</span></h2>
                    </div>
                    <div className="expertise-grid">
                        {expertise.map((e, i) => (
                            <div key={i} className="glass-card expertise-card">
                                <div className="expertise-header">
                                    <div className="icon-box">{e.icon}</div>
                                    <h3>{e.cat}</h3>
                                </div>
                                <ul className="expertise-list">
                                    {e.items.map(item => (
                                        <li key={item}><span className="check-dot" />{item}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Timeline ── */}
            <section className="section timeline-section">
                <div className="container">
                    <div className="text-center mb-64">
                        <span className="section-label">Our Journey</span>
                        <h2 className="section-title">Company <span>Milestones</span></h2>
                    </div>
                    <div className="timeline">
                        {timeline.map((t, i) => (
                            <div key={i} className={`timeline-item ${i % 2 === 1 ? 'right' : ''}`}>
                                <div className="timeline-dot" />
                                <div className="glass-card timeline-card">
                                    <span className="timeline-year">{t.year}</span>
                                    <h4>{t.title}</h4>
                                    <p>{t.desc}</p>
                                </div>
                            </div>
                        ))}
                        <div className="timeline-line" />
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section">
                <div className="container">
                    <div className="cta-banner">
                        <h2>Want to Work <span className="gradient-text">With Us?</span></h2>
                        <p>Let's discuss your project and explore how CoreNuVate IT can become your digital transformation partner.</p>
                        <button className="btn-primary" onClick={() => navigate('/contact')}>
                            <FaArrowRight /> Get in Touch
                        </button>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default AboutPage;
